import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Search,
  Filter,
  Users,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Receipt,
  ArrowRight,
  TrendingUp,
  RefreshCw,
  Building2,
  ExternalLink,
  ChevronRight,
  Sparkles,
  Layers,
  Calendar,
  CreditCard,
  FileSpreadsheet
} from "lucide-react";
import { api } from "../../lib/api";
import { compactMoney, money, badgeTone } from "../../lib/format";
import { toast } from "sonner";
import { isAccountsAuthorized } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";

export default function Accounts({ user, onOpenClientAccounts, onOpenOrder, onNavigate }) {
  const [data, setData] = useState({ metrics: {}, clients: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // "all", "pending_pi", "challans_pending", "challans_received", "pending_invoices", "completed_invoiced"
  const [refreshing, setRefreshing] = useState(false);

  const canManage = isAccountsAuthorized(user);

  const loadData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api("get", `/accounts/dashboard`);
      setData(res.data || { metrics: {}, clients: [] });
    } catch (err) {
      toast.error("Failed to load Accounts dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Client filtering
  const filteredClients = useMemo(() => {
    let list = data.clients || [];
    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((c) => {
        return (
          (c.company_name || "").toLowerCase().includes(q) ||
          (c.contact_person || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.client_id || "").toLowerCase().includes(q) ||
          (c.gstin || "").toLowerCase().includes(q)
        );
      });
    }

    if (activeFilter === "pending_pi") {
      list = list.filter((c) => c.pending_pi_count > 0);
    } else if (activeFilter === "challans_pending") {
      list = list.filter((c) => c.pending_challans_count > 0);
    } else if (activeFilter === "challans_received") {
      list = list.filter((c) => c.received_challans_count > 0);
    } else if (activeFilter === "pending_invoices") {
      list = list.filter((c) => c.pending_invoices_count > 0);
    } else if (activeFilter === "completed_invoiced") {
      list = list.filter((c) => c.completed_count > 0);
    }

    return list;
  }, [data.clients, search, activeFilter]);

  const {
    paginatedItems: paginatedClients,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
  } = usePagination(filteredClients, 15);

  const metrics = data.metrics || {};

  return (
    <div className="content" data-testid="accounts-page" style={{ maxWidth: 1400, margin: "0 auto", paddingBottom: 60 }}>
      {/* Top Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#f0fdf4",
                color: "#16a34a",
                border: "1px solid #bbf7d0",
              }}
            >
              <Receipt size={18} />
            </span>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--navy)" }}>Accounts Management</h2>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Client-wise financial document lifecycle tracking: Proforma Invoices, Challans &amp; Final Invoices
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            className="outline-btn compact"
            onClick={() => loadData(true)}
            disabled={refreshing}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5 }}
            data-testid="accounts-refresh-btn"
          >
            <RefreshCw size={13} className={refreshing ? "spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Summary KPI Cards Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
          marginBottom: 22,
        }}
        data-testid="accounts-summary-cards"
      >
        {/* Card 1: Total Clients */}
        <div
          onClick={() => setActiveFilter("all")}
          style={{
            background: activeFilter === "all" ? "#eff6ff" : "#ffffff",
            border: `1px solid ${activeFilter === "all" ? "#3b82f6" : "var(--line)"}`,
            borderRadius: 8,
            padding: "14px 16px",
            cursor: "pointer",
            boxShadow: activeFilter === "all" ? "0 0 0 1px #3b82f6" : "0 1px 2px rgba(0,0,0,0.02)",
            transition: "all 0.15s ease",
          }}
          data-testid="card-total-clients"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Total Clients
            </span>
            <span style={{ color: "#3b82f6", background: "#dbeafe", padding: 4, borderRadius: 6 }}>
              <Users size={14} />
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--navy)" }}>{metrics.total_clients || 0}</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{metrics.active_clients_with_orders || 0} with active orders</div>
        </div>

        {/* Card 2: Pending Proforma Invoices */}
        <div
          onClick={() => setActiveFilter(activeFilter === "pending_pi" ? "all" : "pending_pi")}
          style={{
            background: activeFilter === "pending_pi" ? "#fffbeb" : "#ffffff",
            border: `1px solid ${activeFilter === "pending_pi" ? "#f59e0b" : "var(--line)"}`,
            borderRadius: 8,
            padding: "14px 16px",
            cursor: "pointer",
            boxShadow: activeFilter === "pending_pi" ? "0 0 0 1px #f59e0b" : "0 1px 2px rgba(0,0,0,0.02)",
            transition: "all 0.15s ease",
          }}
          data-testid="card-pending-pi"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Pending PI
            </span>
            <span style={{ color: "#b45309", background: "#fef3c7", padding: 4, borderRadius: 6 }}>
              <Clock size={14} />
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#b45309" }}>{metrics.pending_pi_count || 0}</div>
          <div style={{ fontSize: 11, color: "#92400e", marginTop: 2 }}>Orders awaiting PI issuance</div>
        </div>

        {/* Card 3: Challans Pending Receipt */}
        <div
          onClick={() => setActiveFilter(activeFilter === "challans_pending" ? "all" : "challans_pending")}
          style={{
            background: activeFilter === "challans_pending" ? "#fdf4ff" : "#ffffff",
            border: `1px solid ${activeFilter === "challans_pending" ? "#c084fc" : "var(--line)"}`,
            borderRadius: 8,
            padding: "14px 16px",
            cursor: "pointer",
            boxShadow: activeFilter === "challans_pending" ? "0 0 0 1px #c084fc" : "0 1px 2px rgba(0,0,0,0.02)",
            transition: "all 0.15s ease",
          }}
          data-testid="card-challans-pending"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#7e22ce", textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Challans Awaiting Receipt
            </span>
            <span style={{ color: "#7e22ce", background: "#f3e8ff", padding: 4, borderRadius: 6 }}>
              <FileSpreadsheet size={14} />
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#7e22ce" }}>{metrics.challans_pending_count || 0}</div>
          <div style={{ fontSize: 11, color: "#6b21a8", marginTop: 2 }}>Issued but not confirmed received</div>
        </div>

        {/* Card 4: Challans Received */}
        <div
          onClick={() => setActiveFilter(activeFilter === "challans_received" ? "all" : "challans_received")}
          style={{
            background: activeFilter === "challans_received" ? "#f0fdf4" : "#ffffff",
            border: `1px solid ${activeFilter === "challans_received" ? "#22c55e" : "var(--line)"}`,
            borderRadius: 8,
            padding: "14px 16px",
            cursor: "pointer",
            boxShadow: activeFilter === "challans_received" ? "0 0 0 1px #22c55e" : "0 1px 2px rgba(0,0,0,0.02)",
            transition: "all 0.15s ease",
          }}
          data-testid="card-challans-received"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Challans Received
            </span>
            <span style={{ color: "#15803d", background: "#dcfce7", padding: 4, borderRadius: 6 }}>
              <CheckCircle2 size={14} />
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#15803d" }}>{metrics.challans_received_count || 0}</div>
          <div style={{ fontSize: 11, color: "#166534", marginTop: 2 }}>Confirmed delivery receipt</div>
        </div>

        {/* Card 5: Pending Final Invoices */}
        <div
          onClick={() => setActiveFilter(activeFilter === "pending_invoices" ? "all" : "pending_invoices")}
          style={{
            background: activeFilter === "pending_invoices" ? "#eff6ff" : "#ffffff",
            border: `1px solid ${activeFilter === "pending_invoices" ? "#0284c7" : "var(--line)"}`,
            borderRadius: 8,
            padding: "14px 16px",
            cursor: "pointer",
            boxShadow: activeFilter === "pending_invoices" ? "0 0 0 1px #0284c7" : "0 1px 2px rgba(0,0,0,0.02)",
            transition: "all 0.15s ease",
          }}
          data-testid="card-pending-invoices"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#0369a1", textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Pending Final Invoices
            </span>
            <span style={{ color: "#0369a1", background: "#e0f2fe", padding: 4, borderRadius: 6 }}>
              <FileText size={14} />
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0369a1" }}>{metrics.pending_invoices_count || 0}</div>
          <div style={{ fontSize: 11, color: "#075985", marginTop: 2 }}>Ready for invoice issuance</div>
        </div>

        {/* Card 6: Completed / Invoiced Orders */}
        <div
          onClick={() => setActiveFilter(activeFilter === "completed_invoiced" ? "all" : "completed_invoiced")}
          style={{
            background: activeFilter === "completed_invoiced" ? "#f8fafc" : "#ffffff",
            border: `1px solid ${activeFilter === "completed_invoiced" ? "#475569" : "var(--line)"}`,
            borderRadius: 8,
            padding: "14px 16px",
            cursor: "pointer",
            boxShadow: activeFilter === "completed_invoiced" ? "0 0 0 1px #475569" : "0 1px 2px rgba(0,0,0,0.02)",
            transition: "all 0.15s ease",
          }}
          data-testid="card-completed-invoiced"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.3px" }}>
              Invoiced / Completed
            </span>
            <span style={{ color: "#475569", background: "#f1f5f9", padding: 4, borderRadius: 6 }}>
              <CheckCircle2 size={14} />
            </span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--navy)" }}>{metrics.completed_invoiced_count || 0}</div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Fully invoiced orders</div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 260 }}>
          <div style={{ position: "relative", width: "100%", maxWidth: 380 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              type="text"
              placeholder="Search by client name, ID, contact, GSTIN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                paddingLeft: 32,
                paddingRight: 10,
                height: 34,
                fontSize: 13,
                borderRadius: 6,
                border: "1px solid var(--line)",
                background: "#f8fafc",
              }}
              data-testid="accounts-search-input"
            />
          </div>

          {activeFilter !== "all" && (
            <button
              className="outline-btn compact"
              onClick={() => setActiveFilter("all")}
              style={{ fontSize: 11.5, height: 32, padding: "0 10px", color: "#64748b" }}
            >
              Clear Filter: <b>{activeFilter.replace("_", " ")}</b>
            </button>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#64748b" }}>
          <span>Showing <b>{filteredClients.length}</b> clients</span>
        </div>
      </div>

      {/* Client Accounts Table */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          border: "1px solid var(--line)",
          borderRadius: 8,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid var(--line)", color: "#64748b", fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                <th style={{ padding: "12px 16px" }}>Client</th>
                <th style={{ padding: "12px 16px" }}>Orders &amp; Value</th>
                <th style={{ padding: "12px 16px" }}>Document Breakdown</th>
                <th style={{ padding: "12px 16px" }}>Pending Action</th>
                <th style={{ padding: "12px 16px", textAlign: "right" }}>Workspace</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                    Loading Accounts data…
                  </td>
                </tr>
              ) : paginatedClients.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                    No clients found matching the current search / filter.
                  </td>
                </tr>
              ) : (
                paginatedClients.map((c) => {
                  const hasPending = c.pending_actions && c.pending_actions.length > 0;
                  return (
                    <tr
                      key={c.client_id}
                      style={{
                        borderBottom: "1px solid var(--line)",
                        cursor: "pointer",
                        transition: "background 0.12s ease",
                      }}
                      onClick={() => onOpenClientAccounts && onOpenClientAccounts(c.client_id)}
                      className="hover-row"
                      data-testid={`client-row-${c.client_id}`}
                    >
                      {/* Client Info */}
                      <td style={{ padding: "14px 16px", minWidth: 220 }}>
                        <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13.5, display: "flex", alignItems: "center", gap: 6 }}>
                          <Building2 size={15} color="#0284c7" />
                          {c.company_name}
                        </div>
                        <div style={{ display: "flex", gap: 8, fontSize: 11.5, color: "#64748b", marginTop: 3 }}>
                          <span>{c.contact_person || "—"}</span>
                          {c.gstin && (
                            <span className="mono" style={{ color: "#475569", background: "#f1f5f9", padding: "1px 5px", borderRadius: 3 }}>
                              GST: {c.gstin}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Orders & Value */}
                      <td style={{ padding: "14px 16px", minWidth: 150 }}>
                        <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>
                          {money(c.total_order_value || 0)}
                        </div>
                        <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                          {c.total_orders} {c.total_orders === 1 ? "Order" : "Orders"}
                        </div>
                      </td>

                      {/* Document Breakdown */}
                      <td style={{ padding: "14px 16px", minWidth: 240 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span
                            className="badge gray"
                            style={{ fontSize: 11, padding: "2px 7px" }}
                            title={`${c.pi_count || 0} Proforma Invoices`}
                          >
                            PI: <b>{c.pi_count || 0}</b>
                          </span>

                          <span
                            className={`badge ${c.pending_challans_count > 0 ? "amber" : "gray"}`}
                            style={{ fontSize: 11, padding: "2px 7px" }}
                            title={`${c.challans_issued_count || 0} Issued, ${c.challans_received_count || 0} Received`}
                          >
                            Challans: <b>{c.challans_received_count || 0}</b> / {c.challans_issued_count || 0}
                          </span>

                          <span
                            className="badge blue"
                            style={{ fontSize: 11, padding: "2px 7px" }}
                            title={`${c.invoices_count || 0} Final Invoices`}
                          >
                            Inv: <b>{c.invoices_count || 0}</b>
                          </span>
                        </div>
                      </td>

                      {/* Pending Action */}
                      <td style={{ padding: "14px 16px", minWidth: 200 }}>
                        {hasPending ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {c.pending_actions.map((act, idx) => (
                              <span
                                key={idx}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: act.includes("PI") ? "#b45309" : act.includes("Challan") ? "#7e22ce" : "#0369a1",
                                  background: act.includes("PI") ? "#fef3c7" : act.includes("Challan") ? "#f3e8ff" : "#e0f2fe",
                                  padding: "2px 7px",
                                  borderRadius: 4,
                                  width: "fit-content",
                                }}
                              >
                                <AlertCircle size={11} /> {act}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              fontSize: 11.5,
                              color: "#15803d",
                              fontWeight: 600,
                            }}
                          >
                            <CheckCircle2 size={13} /> Up to Date
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td style={{ padding: "14px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          className="outline-btn compact"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenClientAccounts && onOpenClientAccounts(c.client_id);
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#0284c7",
                            borderColor: "#bae6fd",
                            background: "#f0f9ff",
                          }}
                          data-testid={`open-client-accounts-${c.client_id}`}
                        >
                          Open Accounts <ChevronRight size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: "12px 16px", borderTop: "1px solid var(--line)" }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={filteredClients.length}
            />
          </div>
        )}
      </div>
    </div>
  );
}
